export const frequencyLabels = {
    MONTHLY: "Monatlich",
    QUARTERLY: "Quartalsweise",
    YEARLY: "Jährlich",
};

export const frequencyOptions = Object.entries(frequencyLabels).map(([value, label]) => ({ value, label }));

export const fmtDate = (value) => {
    if (!value) return "–";
    const date = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("de-DE");
};

export const fmtDateTime = (value) => {
    if (!value) return "–";
    const date = new Date(String(value));
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("de-DE");
};

export const todayInputValue = () => new Date().toISOString().slice(0, 10);

export const normalizeNumberInput = (value, fallback = 0) => {
    if (value === null || value === undefined) return fallback;
    const cleaned = String(value).trim().replace(",", ".");
    if (!cleaned) return fallback;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : fallback;
};

export const toDateInput = (value) => {
    if (!value) return "";
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().slice(0, 10);
    }
    const str = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const parsed = new Date(str);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString().slice(0, 10);
    }
    return "";
};

export const buildEmptyForm = () => ({
    id: null,
    version: null,
    title: "",
    frequency: "MONTHLY",
    nextRun: todayInputValue(),
    endDate: "",
    autoSendMail: false,
    includePayLink: false,
    paymentMethod: "",
    mailSubject: "",
    mailBody: "",
    customerId: "",
    template: {
        invoiceDate: "",
        dueDate: "",
        performanceDate: "",
        currency: "",
        articles: [],
    },
});

export const toFormModel = (dto) => ({
    id: dto.id ?? null,
    version: dto.version ?? null,
    title: dto.title ?? "",
    frequency: dto.frequency ?? "MONTHLY",
    nextRun: toDateInput(dto.nextRun) || todayInputValue(),
    endDate: toDateInput(dto.endDate),
    autoSendMail: Boolean(dto.autoSendMail),
    includePayLink: Boolean(dto.includePayLink),
    paymentMethod: dto.paymentMethod ?? "",
    mailSubject: dto.mailSubject ?? "",
    mailBody: dto.mailBody ?? "",
    customerId: dto.customerId ?? "",
    template: {
        invoiceDate: toDateInput(dto.template?.invoiceDate),
        dueDate: toDateInput(dto.template?.dueDate),
        performanceDate: toDateInput(dto.template?.performanceDate),
        currency: dto.template?.currency ?? "",
        articles: (dto.template?.articles ?? []).map((item) => ({
            articleId: item.articleId ?? "",
            amount: item.amount != null ? String(item.amount) : "1",
            articleDiscount: item.articleDiscount != null ? String(item.articleDiscount) : "0",
            articleDiscountReason: item.articleDiscountReason ?? "",
        })),
    },
});

export const toPayload = (form, baseCurrency) => ({
    id: form.id ?? null,
    version: form.version,
    title: form.title?.trim() || null,
    frequency: form.frequency,
    nextRun: form.nextRun || null,
    endDate: form.endDate || null,
    autoSendMail: form.autoSendMail,
    includePayLink: form.includePayLink,
    paymentMethod: form.paymentMethod?.trim() || null,
    mailSubject: form.mailSubject?.trim() || null,
    mailBody: form.mailBody?.trim() || null,
    customerId: form.customerId || null,
    template: {
        customerId: form.customerId || null,
        invoiceDate: form.template.invoiceDate || null,
        dueDate: form.template.dueDate || null,
        performanceDate: form.template.performanceDate || null,
        parentInvoiceId: null,
        partPercent: null,
        baseAmount: null,
        workOrderId: null,
        totalDiscount: null,
        invoiceType: "STANDARD",
        currency: form.template.currency || baseCurrency || "EUR",
        articles: (form.template.articles || [])
            .filter((item) => item.articleId)
            .map((item) => ({
                articleId: item.articleId,
                amount: normalizeNumberInput(item.amount, 1),
                articleDiscount: normalizeNumberInput(item.articleDiscount, 0),
                articleDiscountReason: item.articleDiscountReason?.trim() || null,
            })),
    },
});

export const sortArticlesByName = (articles) => {
    const list = Array.isArray(articles) ? [...articles] : [];
    const normalize = (value) => String(value || "").trim();
    const keyForArticle = (article) => {
        const name = normalize(article?.name);
        if (name) return name;
        return normalize(article?.articleNumber);
    };
    return list.sort((a, b) => {
        const keyA = keyForArticle(a);
        const keyB = keyForArticle(b);
        if (!keyA && keyB) return 1;
        if (keyA && !keyB) return -1;
        const baseCompare = keyA.localeCompare(keyB, "de", { sensitivity: "base" });
        if (baseCompare !== 0) return baseCompare;
        return normalize(a?.id).localeCompare(normalize(b?.id), "de", { sensitivity: "base" });
    });
};
